import * as Yup from 'yup';
import React, { useEffect } from 'react';
import { useFormik } from 'formik';

import { TextInput, TextInputWithPill, TextInputWithButton, SubmitButton } from '../form-elements';
import { SignupIndividualInputs, SignupIndividualErrors } from '../../../redux/auth/interfaces';
import { withAnalytics, WithAnalyticsProps } from '../../../HOC/with-analytics';

type SignupProps = {
  isCreatingAccount: boolean;
  errors: SignupIndividualErrors;
  signupUser: (data: SignupIndividualInputs) => void;
  getOTP: (phone: string) => void;
  fetchingOTP: boolean;
} & WithAnalyticsProps;

const validationSchema = Yup.object().shape({
  firstName: Yup.string().min(2, 'First Name is too short').required('First Name is required'),
  lastName: Yup.string().min(2, 'Last Name is too short').required('Last Name is required'),
  idNumber: Yup.string()
    .min(6, 'Id Number is too short')
    .required('Id Number is required')
    .matches(/^[0-9]*$/, 'Invalid National ID Number'),
  kraPin: Yup.string()
    .required('Pin Number is required')
    .matches(/^[A-Za-z]\d{9,10}[A-Za-z]$/, 'Pin Number is invalid'),
  phoneNumber: Yup.string()
    .required('Phone Number is required')
    .matches(/^(?:0)?[0]?([1-9][0-9][0-9]{7})$/, 'Phone Number is invalid'),
  // .matches(/^(?:[+]?[1-9]{1,4}|0)?[0]?([0-9]{3}[0-9]{6,8})$/, 'Phone Number is invalid'),
  otp: Yup.string()
    .min(4, 'Verification Code is too short')
    .required('Verification Code is required')
    .matches(/^[0-9]*$/, 'Invalid Verification Code'),
  location: Yup.string().required('Location is required'),
  boxAddress: Yup.string(),
  email: Yup.string().email('Email is invalid').required('Email is required'),
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be contain atleast 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#£\$%\^&\*])(?=.{8,})/,
      'Password must be contain atleast One Uppercase letter, One Lowercase letter, One Number and One special character. Accepted characters are ! @ # £ $ % & * ^',
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Confirm Password is required'),
  acceptTerms: Yup.bool().oneOf([true], 'Accept Ts & Cs is required'),
});

export const SignUpIndividualForm: React.FC<SignupProps> = (props) => {
  const initialValues: SignupIndividualInputs = {
    firstName: '',
    lastName: '',
    idNumber: '',
    kraPin: '',
    phoneNumber: '',
    otp: '',
    email: '',
    location: '',
    boxAddress: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values: SignupIndividualInputs) => {
      props.signupUser({ ...values });
    },
  });

  const _handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    props.sendAnalytics('clickIndividualSignup', 'Auth');
    formik.submitForm();
  };

  useEffect(() => {
    if (!props.isCreatingAccount) {
      formik.setSubmitting(false);
      if (Object.keys(props.errors || {}).length > 0) formik.setErrors(props.errors);
    }
  }, [props.isCreatingAccount]);

  const { values, errors, touched, isSubmitting, ...formProps } = formik;

  return (
    <form className="" onSubmit={_handleSubmit}>
      <div className="flex flex-wrap mt-2 form-row">
        <div className="w-full md:flex-1 md:px-2 md:-ml-2 lg:flex-1 lg:px-2 lg:-ml-2">
          <TextInput
            type="text"
            name="firstName"
            value={values.firstName}
            errors={errors.firstName}
            placeholder="First Name"
            handleBlur={formProps.handleBlur}
            handleChange={formProps.handleChange}
            hasError={errors.firstName && touched.firstName}
          />
        </div>

        <div className="w-full mt-2 md:mt-0 md:flex-1 md:px-2 md:-mr-2 lg:mt-0 lg:flex-1 lg:px-2 lg:-mr-2">
          <TextInput
            type="text"
            name="lastName"
            value={values.lastName}
            errors={errors.lastName}
            placeholder="Last Name"
            handleBlur={formProps.handleBlur}
            handleChange={formProps.handleChange}
            hasError={errors.lastName && touched.lastName}
          />
        </div>
      </div>

      <div className="flex flex-wrap mt-2 form-row md:mt-3">
        <div className="w-full md:flex-1 md:px-2 md:-ml-2 lg:flex-1 lg:px-2 lg:-ml-2">
          <TextInput
            type="text"
            name="idNumber"
            value={values.idNumber}
            errors={errors.idNumber}
            placeholder="National ID Number"
            handleBlur={formProps.handleBlur}
            handleChange={formProps.handleChange}
            hasError={errors.idNumber && touched.idNumber}
          />
        </div>

        <div className="w-full mt-2 md:mt-0 md:flex-1 md:px-2 md:-mr-2 lg:mt-0 lg:flex-1 lg:px-2 lg:-mr-2">
          <TextInput
            type="text"
            name="kraPin"
            value={values.kraPin}
            errors={errors.kraPin}
            placeholder="KRA Pin Number"
            handleBlur={formProps.handleBlur}
            handleChange={formProps.handleChange}
            hasError={errors.kraPin && touched.kraPin}
          />
        </div>
      </div>

      <div className="flex flex-wrap mt-2 form-row md:mt-3">
        <div className="w-full md:flex-1 md:px-2 md:-ml-2 md:-mr-2 lg:flex-1 lg:px-2 lg:-ml-2 lg:-mr-2">
          <TextInputWithPill
            type="text"
            name="phoneNumber"
            value={values.phoneNumber}
            errors={errors.phoneNumber}
            placeholder="Mobile Phone Number"
            handleBlur={formProps.handleBlur}
            handleChange={formProps.handleChange}
            hasError={errors.phoneNumber && touched.phoneNumber}
            pillBg="bg-primary"
          />
        </div>
      </div>

      <div className="flex flex-wrap mt-2 form-row md:mt-3">
        <div className="w-full md:flex-1 md:px-2 md:-ml-2 md:-mr-2 lg:flex-1 lg:px-2 lg:-ml-2 lg:-mr-2">
          <TextInputWithButton
            type="text"
            name="otp"
            value={values.otp}
            errors={errors.otp}
            placeholder="Verification Code(OTP)"
            handleBlur={formProps.handleBlur}
            handleChange={formProps.handleChange}
            hasError={errors.otp && touched.otp}
            pillBg="bg-primary"
            onClick={() => props.getOTP(values.phoneNumber)}
            loading={props.fetchingOTP}
            loadingError={props.errors !== undefined}
            isDisabled={values.phoneNumber === '' || !!errors.phoneNumber}
          />
        </div>
      </div>

      <div className="flex flex-wrap mt-2 form-row md:mt-3">
        <div className="w-full md:flex-1 md:px-2 md:-ml-2 md:-mr-2 lg:flex-1 lg:px-2 lg:-ml-2 lg:-mr-2">
          <TextInput
            type="text"
            name="email"
            value={values.email}
            errors={errors.email}
            placeholder="Email Address"
            handleBlur={formProps.handleBlur}
            handleChange={formProps.handleChange}
            hasError={errors.email && touched.email}
          />
        </div>
      </div>

      <div className="flex flex-wrap mt-2 form-row md:mt-3">
        <div className="w-full md:flex-1 md:px-2 md:-ml-2 lg:flex-1 lg:px-2 lg:-ml-2">
          <TextInput
            type="text"
            name="location"
            value={values.location}
            errors={errors.location}
            placeholder="Location"
            handleBlur={formProps.handleBlur}
            handleChange={formProps.handleChange}
            hasError={errors.location && touched.location}
          />
        </div>

        <div className="w-full mt-2 md:mt-0 md:flex-1 md:px-2 md:-mr-2 lg:mt-0 lg:flex-1 lg:px-2 lg:-mr-2">
          <TextInput
            type="text"
            name="boxAddress"
            value={values.boxAddress}
            errors={errors.boxAddress}
            placeholder="P.O Box Adress (If Applicable)"
            handleBlur={formProps.handleBlur}
            handleChange={formProps.handleChange}
            hasError={errors.boxAddress && touched.boxAddress}
          />
        </div>
      </div>

      <div className="flex flex-wrap mt-2 form-row md:mt-3">
        <div className="w-full md:flex-1 md:px-2 md:-ml-2 md:-mr-2 lg:flex-1 lg:px-2 lg:-ml-2 lg:-mr-2">
          <TextInput
            type="password"
            name="password"
            value={values.password}
            errors={errors.password}
            placeholder="Password"
            handleBlur={formProps.handleBlur}
            handleChange={formProps.handleChange}
            hasError={errors.password && touched.password}
            className="form-group--primary"
          />
        </div>
      </div>

      <div className="flex flex-wrap mt-2 form-row md:mt-3">
        <div className="w-full md:flex-1 md:px-2 md:-ml-2 md:-mr-2 lg:flex-1 lg:px-2 lg:-ml-2 lg:-mr-2">
          <TextInput
            type="password"
            name="confirmPassword"
            value={values.confirmPassword}
            errors={errors.confirmPassword}
            placeholder="Confirm Password"
            handleBlur={formProps.handleBlur}
            handleChange={formProps.handleChange}
            hasError={errors.confirmPassword && touched.confirmPassword}
            className="form-group--primary"
          />
        </div>
      </div>

      <div className="px-4 mt-4 form-row">
        <div>
          <div className="flex items-center mr-4 form-group">
            <input
              id="radio2"
              type="checkbox"
              name="acceptTerms"
              className="hidden"
              value="yes"
              onChange={formProps.handleChange}
            />
            <label htmlFor="radio2" className="flex items-center text-sm font-medium">
              <span className="inline-block w-6 h-6 mr-3 border rounded-full cursor-pointer border-grey flex-no-shrink" />
              <p className="flex-1">
                I have read and agree to the{' '}
                <span>
                  <a className="text-secondary" href="/terms-and-conditions" target="_blank">
                    Terms & Conditions
                  </a>
                </span>{' '}
                and{' '}
                <span>
                  <a className="text-secondary" href="/privacy-policy" target="_blank">
                    Privacy Policy
                  </a>
                </span>
              </p>
            </label>
          </div>
          {errors.acceptTerms && touched.acceptTerms && (
            <p className="pb-2 mt-1 text-xs italic text-red-600">{errors.acceptTerms}</p>
          )}
        </div>
      </div>

      {(errors as SignupIndividualErrors).submit && (
        <div className="p-3 my-4 text-sm font-light text-red-600 bg-red-300 rounded">
          {(errors as SignupIndividualErrors).submit}
        </div>
      )}

      <SubmitButton
        direction="right"
        title="Create Account"
        loadingMsg="Creating Account..."
        isSubmitting={isSubmitting}
        isDirty={formProps.dirty}
        buttonBg="bg-black-primary"
        className="w-full mt-6 md:w-1/2 md:pr-2"
      />
    </form>
  );
};

export default withAnalytics(SignUpIndividualForm);
